import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import SchedulingPage from './SchedulingPage';
import { 
  Check, 
  Users, 
  Building, 
  Factory,
  ChevronRight,
  Shield,
  FileText,
  Eye,
  Clock,
  HelpCircle
} from 'lucide-react';

const PricingPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<'pricing' | 'scheduling'>('pricing');

  const handleScheduleDemo = () => {
    setCurrentPage('scheduling');
  };

  const handleBackToPricing = () => {
    setCurrentPage('pricing');
  };

  if (currentPage === 'scheduling') {
    return <SchedulingPage onBack={handleBackToPricing} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Pricing Plans | Nix Spaces Workplace Phone Monitoring"
        description="Transparent pricing for workplace phone monitoring. Starter, Professional, and Enterprise plans for teams of all sizes. Custom quotes available for your operation."
        canonical="https://nixspaces.com/pricing"
        keywords="workplace monitoring pricing, phone monitoring cost, warehouse safety pricing, enterprise monitoring plans"
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
              <span className="text-2xl font-bold text-gray-900">Spaces</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-600 hover:text-blue-600 transition-colors">Home</Link>
              <Link to="/warehouse-phone-monitoring" className="text-gray-600 hover:text-blue-600 transition-colors">Warehouse</Link>
              <Link to="/industrial-safety-monitoring" className="text-gray-600 hover:text-blue-600 transition-colors">Industrial</Link>
              <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
            <button 
              onClick={handleScheduleDemo}
              className="bg-cyan-500 text-white px-4 py-2 rounded-2xl hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request Demo
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the plan that fits your team size and safety requirements
          </p>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Starter Tier */}
            <div className="bg-white rounded-3xl shadow-lg border-2 border-gray-200 p-8 hover:shadow-xl transition-all duration-300">
              <div className="mb-6">
                <Users className="h-12 w-12 text-cyan-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
                <p className="text-gray-600">For small operations testing workplace monitoring (up to 50 employees)</p>
              </div>
              
              <div className="mb-8">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  Contact for Pricing
                </div>
                <p className="text-gray-500">Per employee per month</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Up to 50 employees</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Real-time activity monitoring</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Basic compliance reports</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Manager dashboard access</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Email support</span>
                </li>
              </ul>

              <button 
                onClick={handleScheduleDemo}
                className="w-full bg-white text-cyan-600 border-2 border-cyan-600 px-6 py-3 rounded-2xl font-semibold hover:bg-cyan-600 hover:text-white transition-all duration-300"
              >
                Get Started
              </button>
            </div>

            {/* Professional Tier - Featured */}
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl shadow-xl border-2 border-cyan-500 p-8 transform md:scale-105 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-amber-400 text-gray-900 px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              
              <div className="mb-6">
                <Building className="h-12 w-12 text-white mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
                <p className="text-cyan-100">For warehouse/logistics operations with multi-site oversight (51-200 employees)</p>
              </div>
              
              <div className="mb-8">
                <div className="text-4xl font-bold text-white mb-2">
                  Contact for Pricing
                </div>
                <p className="text-cyan-100">Per employee per month</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                  <span className="text-white">51-200 employees</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                  <span className="text-white">All Starter features</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                  <span className="text-white">Advanced analytics & reporting</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                  <span className="text-white">Multi-site management</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                  <span className="text-white">Custom alert thresholds</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-white mt-0.5 flex-shrink-0" />
                  <span className="text-white">Priority support</span>
                </li>
              </ul>

              <button 
                onClick={handleScheduleDemo}
                className="w-full bg-white text-cyan-600 px-6 py-3 rounded-2xl font-semibold hover:bg-cyan-50 transition-all duration-300 shadow-lg"
              >
                Get Started
              </button>
            </div>

            {/* Enterprise Tier */}
            <div className="bg-white rounded-3xl shadow-lg border-2 border-gray-200 p-8 hover:shadow-xl transition-all duration-300">
              <div className="mb-6">
                <Factory className="h-12 w-12 text-cyan-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
                <p className="text-gray-600">For large industrial/manufacturing operations with complex compliance needs (200+ employees)</p>
              </div>
              
              <div className="mb-8">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  Custom Pricing
                </div>
                <p className="text-gray-500">Volume discounts available</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">200+ employees</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">All Professional features</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Dedicated account manager</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Custom integrations (HRIS, EHS)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">API access</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">24/7 premium support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">On-premise deployment option</span>
                </li>
              </ul>

              <button 
                onClick={handleScheduleDemo}
                className="w-full bg-white text-cyan-600 border-2 border-cyan-600 px-6 py-3 rounded-2xl font-semibold hover:bg-cyan-600 hover:text-white transition-all duration-300"
              >
                Contact Sales
              </button>
            </div>
          </div>

          {/* Pricing Note */}
          <div className="mt-12 bg-blue-50 border border-blue-100 rounded-2xl p-6 text-center">
            <p className="text-gray-700">
              <strong>Note:</strong> All pricing tiers are example structures. Final pricing is customized based on your team size, features required, and deployment needs. Contact us for a detailed quote.
            </p>
          </div>
        </div>
      </section>

      {/* All Plans Include */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              All Plans Include
            </h2>
            <p className="text-xl text-gray-600">
              Core safety and compliance features standard across all tiers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-md text-center">
              <Clock className="h-10 w-10 text-cyan-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Work Hours Only</h3>
              <p className="text-sm text-gray-600">Monitoring during shifts, not breaks or off-hours</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md text-center">
              <Shield className="h-10 w-10 text-cyan-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Privacy Protection</h3>
              <p className="text-sm text-gray-600">No content access, only activity timestamps</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md text-center">
              <FileText className="h-10 w-10 text-cyan-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Compliance Logs</h3>
              <p className="text-sm text-gray-600">Automated documentation for audits</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md text-center">
              <Eye className="h-10 w-10 text-cyan-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Manager Dashboard</h3>
              <p className="text-sm text-gray-600">Real-time visibility for supervisors</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pricing FAQs
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <div className="flex items-start space-x-3">
                <HelpCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">What counts as an "employee" for pricing?</h3>
                  <p className="text-gray-600">
                    Any team member with the monitoring app installed on their device. Pricing is typically based on active monitored users per month.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <div className="flex items-start space-x-3">
                <HelpCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Is there a contract or can I cancel anytime?</h3>
                  <p className="text-gray-600">
                    We offer flexible monthly billing and annual plans with discounts. Contact us to discuss contract terms that fit your needs.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <div className="flex items-start space-x-3">
                <HelpCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Do you offer a free trial?</h3>
                  <p className="text-gray-600">
                    Yes! We offer pilot programs and trials for qualified organizations. Schedule a demo to learn more about trial options.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <div className="flex items-start space-x-3">
                <HelpCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">What about implementation and training?</h3>
                  <p className="text-gray-600">
                    Setup assistance and manager training are included with Professional and Enterprise plans. Starter plans include self-service onboarding resources.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <div className="flex items-start space-x-3">
                <HelpCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">How does billing work for fluctuating team sizes?</h3>
                  <p className="text-gray-600">
                    Enterprise plans can include flexible billing based on actual active users. We work with you to accommodate seasonal workforce changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy & Trust Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-lg border border-gray-100">
            <div className="text-center mb-8">
              <Shield className="h-16 w-16 text-cyan-600 mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Privacy & Employee Trust
              </h2>
              <p className="text-lg text-gray-600">
                Nix Spaces is designed with clear boundaries to protect employee privacy while ensuring workplace safety.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100">
                <h3 className="font-semibold text-gray-900 mb-2">✓ Work Hours Only</h3>
                <p className="text-gray-700">Monitoring activates during scheduled shifts and pauses during breaks, lunch, and off-hours.</p>
              </div>

              <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100">
                <h3 className="font-semibold text-gray-900 mb-2">✓ Clear Employee Notice</h3>
                <p className="text-gray-700">Transparent disclosure to all monitored employees with on-screen indicators when monitoring is active.</p>
              </div>

              <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100">
                <h3 className="font-semibold text-gray-900 mb-2">✓ No Content Monitoring</h3>
                <p className="text-gray-700">We track activity timestamps and duration only—no message content, photos, or personal data access.</p>
              </div>

              <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100">
                <h3 className="font-semibold text-gray-900 mb-2">✓ Compliance-Ready Logs</h3>
                <p className="text-gray-700">Secure, timestamped records designed for safety audits, not intrusive surveillance.</p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link to="/privacy" className="text-cyan-600 hover:text-cyan-700 font-semibold inline-flex items-center space-x-2">
                <span>Read our full Privacy Policy</span>
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Schedule a demo to discuss your specific needs and get a custom quote.
          </p>
          <button 
            onClick={handleScheduleDemo}
            className="bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl mx-auto"
          >
            <span>Schedule Demo</span>
            <ChevronRight className="h-5 w-5" />
          </button>
          <p className="text-gray-500 mt-4">No credit card required • 15-minute consultation</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-8 w-auto filter invert" />
              <span className="text-xl font-bold">Spaces</span>
            </Link>
            
            <div className="flex flex-wrap items-center justify-center space-x-6 text-sm">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/warehouse-phone-monitoring" className="text-gray-400 hover:text-white transition-colors">
                Warehouse
              </Link>
              <Link to="/industrial-safety-monitoring" className="text-gray-400 hover:text-white transition-colors">
                Industrial
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <span className="text-gray-400">
                © 2024 Nix Spaces. All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
